// src/components/NewsPostModal.jsx
import { useEffect, useState, useRef } from "react";
import { X, Calendar, User, Tag } from "lucide-react";
import { API } from "../utils/constants";
import { PostBanner } from "./AdminNewsPage";

export default function NewsPostModal({ post, onClose }) {
  const [full, setFull]       = useState(null);
  const [loading, setLoading] = useState(true);
  const iframeRef             = useRef(null);

  // fetch full post
  useEffect(() => {
    fetch(`${API}/news/${post.slug}`)
      .then(r => r.json())
      .then(d => setFull(d.post || null))
      .catch(() => setFull(null))
      .finally(() => setLoading(false));
  }, [post.slug]);

  // ESC to close
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // lock body scroll while modal is open — fixes double scrollbar
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const meta       = full || post;
  const bannerData = {
    ...meta,
    banner_type:    post.banner_type    || meta.banner_type,
    banner_image:   post.banner_image   || meta.banner_image,
    banner_youtube: post.banner_youtube || meta.banner_youtube,
    banner_icon:    post.banner_icon    || meta.banner_icon,
    banner_color:   post.banner_color   || meta.banner_color,
  };
  const tags = typeof meta.tags === "string"
    ? JSON.parse(meta.tags || "[]")
    : (meta.tags || []);

  const isFullHTML = full?.body && full.body.includes("<!--FULLSCREEN-->");

  // ── FULLSCREEN HTML posts — pure iframe, floating close only
  if (!loading && isFullHTML) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "#0a0810", display: "flex", flexDirection: "column" }}>
        <button
          onClick={onClose}
          style={{
            position: "fixed", top: 16, right: 16, zIndex: 1001,
            width: 40, height: 40, borderRadius: "50%",
            background: "rgba(10,8,16,0.85)", border: "1px solid rgba(201,168,76,0.4)",
            color: "#c9a84c", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(8px)",
            boxShadow: "0 0 20px rgba(0,0,0,0.6), 0 0 12px rgba(201,168,76,0.15)",
            transition: "all .2s",
          }}
          onMouseOver={e => { e.currentTarget.style.background = "rgba(201,168,76,0.15)"; e.currentTarget.style.borderColor = "rgba(201,168,76,0.8)"; }}
          onMouseOut={e =>  { e.currentTarget.style.background = "rgba(10,8,16,0.85)";   e.currentTarget.style.borderColor = "rgba(201,168,76,0.4)"; }}
        >
          <X style={{ width: 16, height: 16 }} />
        </button>
        <iframe
          ref={iframeRef}
          srcDoc={full.body}
          style={{ flex: 1, width: "100%", height: "100%", border: "none", display: "block" }}
          title="post-content"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    );
  }

  // ── STANDARD posts — styled dark/gold modal
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, zIndex: 500,
        background: "rgba(5,5,10,0.88)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "flex-start", justifyContent: "center",
        overflow: "hidden",           // ← only the inner panel scrolls
        padding: "40px 20px",
        animation: "toa-fadein .18s ease",
      }}
    >
      <div
        className="toa-news-panel"
        style={{
          width: "100%", maxWidth: 760,
          maxHeight: "calc(100vh - 80px)",
          overflowY: "auto",
          background: "linear-gradient(160deg,#0d0a1c,#0a0810)",
          border: "1px solid rgba(201,168,76,0.2)",
          borderRadius: 14,
          position: "relative",
          boxShadow: "0 0 0 1px rgba(201,168,76,0.08), 0 32px 80px rgba(0,0,0,0.85)",
          animation: "toa-slidein .22s ease",
        }}
      >
        {/* Gold top bar accent */}
        <div style={{
          height: 3, borderRadius: "14px 14px 0 0",
          background: "linear-gradient(90deg, transparent, rgba(201,168,76,0.6) 30%, rgba(201,168,76,0.9) 50%, rgba(201,168,76,0.6) 70%, transparent)",
        }} />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute", top: 16, right: 16, zIndex: 10,
            width: 32, height: 32, borderRadius: 6,
            background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,168,76,0.2)",
            color: "rgba(201,168,76,0.7)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: ".15s",
          }}
          onMouseOver={e => { e.currentTarget.style.background = "rgba(201,168,76,0.1)"; e.currentTarget.style.color = "#c9a84c"; }}
          onMouseOut={e =>  { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "rgba(201,168,76,0.7)"; }}
        >
          <X style={{ width: 13, height: 13 }} />
        </button>

        {/* Banner */}
        <PostBanner post={bannerData} />

        {/* Content */}
        <div style={{ padding: "28px 32px 40px" }}>

          {/* Meta row */}
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--muted)", fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>
              <User style={{ width: 11, height: 11 }} />
              {meta.author}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--muted)", fontFamily: "'Cinzel',serif", letterSpacing: 1 }}>
              <Calendar style={{ width: 11, height: 11 }} />
              {new Date(meta.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
            {tags.length > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <Tag style={{ width: 11, height: 11, color: "var(--muted)" }} />
                {tags.map(t => <span key={t} className="toa-tag">{t}</span>)}
              </span>
            )}
          </div>

          {/* Ornamental divider */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,rgba(201,168,76,0.4),transparent)" }} />
            <div style={{ width: 5, height: 5, background: "#c9a84c", transform: "rotate(45deg)", boxShadow: "0 0 8px rgba(201,168,76,0.5)" }} />
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,transparent,rgba(201,168,76,0.4))" }} />
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: "clamp(18px, 3vw, 28px)",
            fontWeight: 900,
            color: "#f0d878",
            marginBottom: 14,
            lineHeight: 1.2,
            textShadow: "0 0 40px rgba(201,168,76,0.3)",
          }}>
            {meta.title}
          </h1>

          {/* Excerpt */}
          <p style={{
            fontSize: 15,
            fontFamily: "'IM Fell English', serif",
            fontStyle: "italic",
            color: "rgba(168,156,200,0.8)",
            lineHeight: 1.78,
            marginBottom: 28,
            paddingBottom: 28,
            borderBottom: "1px solid rgba(201,168,76,0.1)",
          }}>
            {meta.excerpt}
          </p>

          {/* Body */}
          {loading ? (
            <div style={{ color: "var(--muted)", fontSize: 13, padding: "24px 0", textAlign: "center", fontFamily: "'Cinzel',serif", letterSpacing: 2 }}>
              Loading…
            </div>
          ) : full?.body ? (
            <div
              className="toa-post-body"
              style={{ fontSize: 14, color: "var(--text2)", lineHeight: 1.85 }}
              dangerouslySetInnerHTML={{ __html: full.body }}
            />
          ) : (
            <div style={{ color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>No additional content.</div>
          )}

          {/* Bottom seal line */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 36 }}>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,rgba(201,168,76,0.2),transparent)" }} />
            <span className="toa-logo">The Ones Above</span>
            <div style={{ flex: 1, height: 1, background: "linear-gradient(90deg,transparent,rgba(201,168,76,0.2))" }} />
          </div>

        </div>
      </div>
    </div>
  );
}